import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { BOOK_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const BOX = { boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" };

function CampaignFunnel() {
  const stages = [
    { label: "Budskab", note: "Defineret" },
    { label: "Målgruppe", note: "Segmenteret" },
    { label: "Annonce", note: "LinkedIn · Meta" },
    { label: "Landingpage", note: "Konvertering" },
    { label: "Lead / salg", note: "Klar" },
  ];
  return (
    <div aria-hidden className="rounded-2xl border border-border bg-card p-5 sm:p-6 select-none" style={BOX}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
            <span className="relative h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />
          </span>
          <span className="text-[13px] font-medium text-foreground">Kampagneflow</span>
        </div>
        <span className="rounded-full border border-border px-3 py-1 text-[11px] tracking-wide text-muted-foreground">Aktiv</span>
      </div>
      <div className="relative pl-6">
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border" />
        <div className="space-y-3">
          {stages.map((s, i) => {
            const last = i === stages.length - 1;
            return (
              <div key={s.label} className="relative flex items-center gap-3 rounded-xl border border-border bg-secondary/60 px-3.5 py-2.5">
                <span className="absolute -left-6 flex h-[18px] w-[18px] items-center justify-center rounded-full border bg-card"
                  style={{ borderColor: last ? TEAL : "#DDD8D0", backgroundColor: last ? TEAL : "#FFFFFF" }}>
                  {last ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : <span className="text-[9px] text-muted-foreground">{i + 1}</span>}
                </span>
                <span className="text-[13px] text-foreground">{s.label}</span>
                <span className="ml-auto text-[11px] text-muted-foreground">{s.note}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


const HELP_WITH = [
  "Kampagnestrategi", "Målgruppeanalyse", "Budskaber", "Kreativer",
  "Annoncetekster", "Landingpages", "Løbende optimering", "Rapportering",
];

const STEPS = [
  { title: "Vi lærer jeres forretning at kende", desc: "Vi sætter os ind i jeres målgruppe, produkter, konkurrenter og forretningsmål." },
  { title: "Vi udvikler kampagnen", desc: "Ved hjælp af både erfaring og AI udvikler vi målgrupper, budskaber, kreative idéer og annoncevarianter, der giver de bedste forudsætninger for resultater." },
  { title: "Vi tester og optimerer", desc: "Vi overvåger kampagnerne løbende og bruger data og AI til hurtigt at identificere, hvad der virker - og hvor der er potentiale for forbedringer." },
];

const BENEFITS = [
  "Strategisk rådgivning - ikke kun kampagneopsætning",
  "AI understøtter analyse, produktion og optimering",
  "Hurtigere udvikling af kampagner",
  "Flere kreative idéer og annoncevarianter",
  "Løbende optimering baseret på data",
  "Fokus på leads, salg og forretningsværdi",
];

export default function PaidSocialPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Paid Social</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            Annoncering, <span style={{ color: "#22D3C5" }}>der skaber synlighed, leads og kunder</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 space-y-4 text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
            <p>De bedste annoncer rammer de rigtige mennesker med det rigtige budskab på det rigtige tidspunkt - på platforme som LinkedIn, Facebook og Instagram, hvor jeres kunder allerede er.</p>
            <p>Hos Aicom kombinerer vi strategisk kommunikation med AI-understøttede analyser og kreative processer. Det gør os i stand til hurtigere at teste budskaber, udvikle kampagner og optimere annoncer - uden at gå på kompromis med kvaliteten.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
            <MButton href="#hjaelp" size="lg" variant="secondary" data-testid="hero-hjaelp">Se hvad vi hjælper med</MButton>
          </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <CampaignFunnel />
          </motion.div>
          </div>
        </div>
      </section>

      {/* Mere end annoncer */}
      <section id="hjaelp" className="relative z-10 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Mere end annoncer</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-6">Paid Social handler ikke kun om at oprette kampagner</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed mb-10">
            <p>Det handler om at forstå målgruppen, udvikle de rigtige budskaber og løbende optimere, så annoncerne skaber forretningsmæssige resultater.</p>
            <p className="text-foreground">Vi hjælper blandt andet med:</p>
          </div>
          <div className="flex flex-wrap gap-2.5 mb-10">
            {HELP_WITH.map((h, i) => (
              <motion.span key={h} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 4) * 0.05 }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-foreground" /> {h}
              </motion.span>
            ))}
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">AI er en naturlig del af processen, hvor den hjælper os med analyse, idéudvikling, test og produktion. Den strategiske retning og den endelige kvalitet sikres altid af vores rådgivere.</p>
        </div>
      </section>

      {/* Sådan arbejder vi */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Sådan arbejder vi</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra forretning til resultater</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}>
              <div className="text-5xl font-display font-medium text-muted-foreground/25">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-lg font-medium mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Derfor vælger virksomheder Aicom */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Derfor vælger virksomheder Aicom</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Annoncering med strategi og høj eksekveringskraft</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BENEFITS.map((b, i) => (
              <motion.div key={b} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 2) * 0.06 }}
                className="flex items-center gap-3 rounded-xl border border-border bg-background px-5 py-4">
                <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5 text-background" strokeWidth={2.5} />
                </div>
                <span className="text-foreground">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kombinér med Aicom AI */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex h-12 w-12 rounded-xl bg-secondary items-center justify-center mb-6">
          <Sparkles className="h-5 w-5 text-foreground" strokeWidth={1.75} />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-5">Kombinér med Aicom AI</h2>
        <div className="space-y-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          <p>Ønsker I selv at producere mere indhold, kan Paid Social kombineres med Aicom AI. Platformen kan blandt andet hjælpe med at udvikle kampagneidéer, skrive annonceudkast, producere landingpages og generere nye vinkler, som efterfølgende kan anvendes i jeres annoncering.</p>
        </div>
        <div className="mt-8">
          <MButton to="/losninger/aicom-ai" variant="secondary" data-testid="link-aicom-ai">Læs om Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
